import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isOwningSourceId } from '@/lib/problems/attribution'
import { type RouterInputs, trpc } from '@/lib/trpc'
import { toastError, toastSuccess } from '@/lib/utils/toast-helpers'
import type { ProblemEditorData } from '../problem-editor'
import type { ProblemSource, Source } from '../types'
import { DEFAULT_SOURCES } from '../types'
import { NewProblemSourceForm } from './new-problem-source-form'
import { ProblemSourcesManager } from './problem-sources-manager'

/**
 * The fields `updateProblemSource` actually writes.
 *
 * Narrower than `keyof ProblemSource` on purpose: the mutation forwards these
 * and nothing else, so a save of `order` or `sourceSlug` would resolve
 * successfully, persist nothing, and let the local edit vanish at the next
 * refetch — a silent no-op with a success toast. Every declaration on the way
 * to a save uses this, so that call is a type error instead.
 *
 * Read off the router's own input rather than transcribed, because a
 * transcript only drifts in the direction that hurts: drop a field there and a
 * hand-written union still calls it savable, restoring the exact no-op this
 * exists to prevent. The two addressing keys come off by name — `sourceId`
 * cannot be inferred away, since `ProblemSource` has a field of that name
 * meaning something else entirely (its FK to `Source`) — and the `Extract`
 * then drops any future input field the row does not carry.
 *
 * It lives here rather than beside `ProblemSource` in `../types` even though
 * two children import it from their own container: `../types` is imported by
 * `api/_trpc/routers/admin/clients.ts`, so pulling `RouterInputs` in there
 * would close a type-level cycle through `AppRouter`.
 */
type UpdateSourceInput =
  RouterInputs['admin']['questions']['updateProblemSource']
export type SavableSourceField = Extract<
  keyof Omit<UpdateSourceInput, 'problemId' | 'sourceId'>,
  keyof ProblemSource
>

interface SourcesSectionProps {
  problemId?: string
  formData: ProblemEditorData
  sources: ProblemSource[]
  onSourcesUpdate: (sources: ProblemSource[]) => void
  onFormUpdate: (updates: Partial<ProblemEditorData>) => void
}

export function SourcesSection({
  problemId,
  formData,
  sources: initialSources,
  onSourcesUpdate,
  onFormUpdate,
}: SourcesSectionProps) {
  const [sources, setSources] = useState<ProblemSource[]>(initialSources)
  const [expandedSourceId, setExpandedSourceId] = useState<string | null>(null)
  // Auto-expand the owning source once per problem, on first data arrival.
  // This component stays mounted across problem switches (ProblemEditor keys
  // off route params, not a remount), so we key the guard on `problemId`: a
  // bare boolean would fire once ever (skipping later problems) and the
  // previous problem's expanded card would leak into the next. Keying on the
  // id re-arms per problem; not depending on `expandedSourceId` keeps a manual
  // collapse from re-triggering the expand.
  const autoExpandedProblemIdRef = useRef<string | null>(null)
  const [savingSource, setSavingSource] = useState(false)

  // For new problems, track the source selection
  const [selectedSourceType, setSelectedSourceType] = useState<string>(
    formData.source || 'Beyond Cracking the Coding Interview',
  )

  // Modal state for adding new sources
  const [addSourceModalOpen, setAddSourceModalOpen] = useState(false)

  // Sources that should NOT show the highest external ID hint
  const EXCLUDED_SOURCES = [
    'LeetCode',
    'Beyond Cracking the Coding Interview',
    'Cracking the Coding Interview',
  ]

  // Check if current source should show the highest external ID hint
  const shouldShowHighestId = useMemo(() => {
    return !EXCLUDED_SOURCES.includes(selectedSourceType)
  }, [selectedSourceType])

  // Ref to hold current sources for use in callbacks (avoids stale closures)
  const sourcesRef = useRef(sources)
  useEffect(() => {
    sourcesRef.current = sources
  }, [sources])

  // ============================================
  // tRPC queries
  // ============================================

  // Fetch available sources from API (replaces manual state+useEffect+fetch)
  const {
    data: availableSourcesFromApi = [],
    isLoading: loadingAvailableSources,
  } = trpc.admin.sources.list.useQuery()

  // Fetch highest external ID when source changes (for new problems only)
  const { data: highestIdData, isLoading: loadingHighestId } =
    trpc.admin.sources.highestExternalId.useQuery(
      { id: formData.sourceId! },
      {
        enabled: !!formData.sourceId && !problemId && shouldShowHighestId,
      },
    )

  const highestNumericId = highestIdData?.highestNumericId ?? null
  const highestAlphanumericId = highestIdData?.highestAlphanumericId ?? null

  // ============================================
  // tRPC mutations
  // ============================================

  const createProblemSourceMutation =
    trpc.admin.questions.createProblemSource.useMutation()
  const deleteProblemSourceMutation =
    trpc.admin.questions.deleteProblemSource.useMutation()
  const updateProblemSourceMutation =
    trpc.admin.questions.updateProblemSource.useMutation()

  const utils = trpc.useUtils()

  // Sync selectedSourceType when API sources load (for new problems only)
  // This prevents the dropdown value from becoming stale when API sources
  // don't include the initial default value
  useEffect(() => {
    if (!problemId && availableSourcesFromApi.length > 0) {
      const currentSourceExists = availableSourcesFromApi.some(
        (s) => s.name === selectedSourceType,
      )
      if (!currentSourceExists) {
        // Select the first available source from API
        const firstSource = availableSourcesFromApi[0]
        setSelectedSourceType(firstSource.name ?? '')
        onFormUpdate({
          source: firstSource.name ?? undefined,
          sourceId: firstSource.id,
          sourceIcon: firstSource.icon,
        })
      } else {
        // Current source exists - make sure sourceId is set
        const matchingSource = availableSourcesFromApi.find(
          (s) => s.name === selectedSourceType,
        )
        if (matchingSource && !formData.sourceId) {
          onFormUpdate({
            sourceId: matchingSource.id,
          })
        }
      }
    }
  }, [
    availableSourcesFromApi,
    problemId,
    selectedSourceType,
    formData.sourceId,
    onFormUpdate,
  ])

  // Expand the owning source by default for existing problems. Identify it by
  // the synthetic `owning:` id marker (matching metadata-section and
  // problem-sources-manager) rather than array position: when a problem has no
  // owning source, attribution rows start at order 1 and `sources[0]` would
  // open the wrong card.
  useEffect(() => {
    if (!problemId || autoExpandedProblemIdRef.current === problemId) return
    // Read `initialSources` (the prop), not local `sources`: on a problem
    // switch `problemId` and `initialSources` update together, whereas local
    // `sources` lags a render behind (it's synced from the prop in a separate
    // effect), so keying off it would mark the new problem handled using the
    // previous problem's rows and never open the right owning source.
    if (initialSources.length === 0) return
    autoExpandedProblemIdRef.current = problemId
    // Reset expansion for the new problem (don't leak the previous problem's
    // open card), then auto-expand its owning source if it has one.
    const owningSource = initialSources.find((s) => isOwningSourceId(s.id))
    setExpandedSourceId(owningSource ? owningSource.id : null)
  }, [problemId, initialSources])

  // Sync sources from props when they change
  useEffect(() => {
    setSources(initialSources)
  }, [initialSources])

  // Handle adding a new source (for existing problems)
  // If sourceId is provided directly, it bypasses the lookup in availableSourcesFromApi
  // (useful when adding a newly created source before React state has updated)
  const handleAddSource = useCallback(
    async (
      sourceName: string,
      sourceIcon?: string | null,
      sourceId?: string,
    ) => {
      if (!problemId) return

      // Check if source already exists
      if (sources.some((s) => s.source === sourceName)) {
        toastError(null, `Source "${sourceName}" already exists`)
        return
      }

      // Use provided sourceId or find from available sources
      let resolvedSourceId = sourceId
      if (!resolvedSourceId) {
        const sourceRecord = availableSourcesFromApi.find(
          (s) => s.name === sourceName,
        )
        if (!sourceRecord) {
          toastError(
            null,
            `Source "${sourceName}" not found. Please create it first.`,
          )
          return
        }
        resolvedSourceId = sourceRecord.id
      }

      setSavingSource(true)
      try {
        const result = await createProblemSourceMutation.mutateAsync({
          problemId,
          sourceId: resolvedSourceId,
          title: formData.name, // Default to problem name
        })

        if (!result.source) {
          throw new Error('Failed to create source')
        }

        const newSources = [...sources, result.source].sort(
          (a, b) => a.order - b.order,
        )
        setSources(newSources)
        onSourcesUpdate(newSources)
        toastSuccess(`Added ${sourceName} source`)
      } catch (error) {
        toastError(error, 'Failed to add source')
      } finally {
        setSavingSource(false)
      }
    },
    [
      problemId,
      formData.name,
      sources,
      availableSourcesFromApi,
      createProblemSourceMutation,
      onSourcesUpdate,
    ],
  )

  // Handle deleting a source
  const handleDeleteSource = useCallback(
    async (sourceId: string) => {
      if (!problemId) return

      const sourceToDelete = sources.find((s) => s.id === sourceId)
      if (!sourceToDelete) return

      if (sourceToDelete.order === 0 || isOwningSourceId(sourceToDelete.id)) {
        toastError(null, 'Cannot delete the owning source')
        return
      }

      // eslint-disable-next-line no-alert -- Intentional confirmation dialog
      const confirmed = window.confirm(
        `Delete source "${sourceToDelete.source}"? This action cannot be undone.`,
      )
      if (!confirmed) return

      try {
        await deleteProblemSourceMutation.mutateAsync({
          problemId,
          sourceId,
        })

        // Reindex attribution rows to sequential order values (matching the
        // server's index+1 scheme) so deletions don't leave gaps; the synthetic
        // owning row stays at order 0.
        let attributionOrder = 1
        const newSources = sources
          .filter((s) => s.id !== sourceId)
          .map((s) =>
            isOwningSourceId(s.id) || s.order === 0
              ? s
              : { ...s, order: attributionOrder++ },
          )
        setSources(newSources)
        onSourcesUpdate(newSources)
        toastSuccess(`Deleted ${sourceToDelete.source} source`)
      } catch (error) {
        toastError(error, 'Failed to delete source')
      }
    },
    [problemId, sources, deleteProblemSourceMutation, onSourcesUpdate],
  )

  /**
   * Update local source state (called on every keystroke).
   *
   * Local only, and single-row: the debounced inputs use this on their way to
   * a save that may never come. A control that saves the moment it is touched
   * wants `handleCommitSourceField` below instead, which owns the edit, the
   * save, and the undo together.
   *
   * Restricted to `SavableSourceField` for the same reason the save path is:
   * every use here is en route to a save, so a field the save cannot forward
   * would leave the edit in local state until the `initialSources` sync effect
   * silently discards it. Narrowing only the save half left that door open
   * beside it — an input wired with `onChange` alone would never have erred.
   *
   * `sourcesRef` is advanced here rather than left to its effect a render
   * later, so a commit landing in the same tick starts from the typed value
   * instead of overwriting it.
   */
  const handleLocalSourceUpdate = useCallback(
    <K extends SavableSourceField>(
      sourceId: string,
      field: K,
      value: ProblemSource[K],
    ) => {
      const next = sourcesRef.current.map((s) =>
        s.id === sourceId ? { ...s, [field]: value } : s,
      )
      sourcesRef.current = next
      setSources(next)
    },
    [],
  )

  /**
   * Save one field of one row, and adopt the row the server hands back.
   *
   * Owns the failure toast for every save path, its own and the commit's
   * alike. It used to live at each call site, which meant four copies in the
   * card and a fifth here, all saying "Failed to save" — and any new control
   * that forgot the try/catch would fail in total silence. Rethrows, because
   * `AutoSaveInput` and `AutoSaveToggle` both drive their own error state off
   * a rejected promise.
   *
   * Sends the one field being saved and nothing else. The mutation is a partial
   * patch — every field optional, every write guarded by
   * `if (input.x !== undefined)` — so echoing the row's other values back
   * bought nothing and cost a lost update: the editor's copy of a row goes
   * stale the moment another admin edits it, and every save re-wrote all five
   * fields from that stale copy. Writing the field by key also means the
   * payload can never fall behind `SavableSourceField`, which is derived from
   * this mutation's own input and grows on its own.
   */
  const handleSaveSourceField = useCallback(
    async <K extends SavableSourceField>(
      sourceId: string,
      field: K,
      value: ProblemSource[K],
    ) => {
      if (!problemId) return

      // An existence check, read off the ref rather than the render closure:
      // a row deleted while a debounced save was pending has nothing to save.
      if (!sourcesRef.current.some((s) => s.id === sourceId)) return

      try {
        const result = await updateProblemSourceMutation.mutateAsync({
          problemId,
          sourceId,
          // Written by key rather than picked out per field: `field === 'title'`
          // cannot narrow `value`, because narrowing a generic key does not
          // narrow the value correlated with it, so naming the fields meant an
          // `as` per field that the checker could never verify.
          [field]: value,
        })

        // Bound before the callback, not re-asserted inside it: narrowing a
        // property does not survive into a closure, and a `!` there would be a
        // claim the guard above can no longer be seen to support.
        const savedSource = result.source
        if (!savedSource) return

        // One field in, one field out — and only if that field has not moved
        // since. The response is a whole row, but the request patched a single
        // column, so every *other* column of it is the database's, stale with
        // respect to anything typed since; and the column it did write answers
        // a request that two more seconds of typing may already have overtaken.
        // These inputs are fully controlled off this state and debounce for two
        // seconds, so either one erases text out from under the caret — type a
        // title and flip Premium inside the window, or simply keep typing while
        // a save is in flight. Adopting the saved field when it still holds
        // what was sent keeps the server's trim without letting anything travel
        // backwards.
        //
        // Marking an original also clears whatever row held the mark, but that
        // half belongs to `handleCommitSourceField` — the only caller that may
        // set `isOriginal`, and the only one that can undo it — which has
        // already written the cleared rows into `sourcesRef.current` before the
        // save begins.
        //
        // Applied once, to one array, and handed to both consumers: passing
        // `setSources` a transform while handing the parent the result of the
        // same transform over `sourcesRef.current` fed two different arrays —
        // React's latest state and a ref synced a render behind — and the
        // parent's copy is echoed back into `sources`, so the stale one won.
        // The ref is advanced here rather than waiting for its effect, so
        // back-to-back saves compose instead of each starting from the same
        // snapshot.
        const next = sourcesRef.current.map((s) =>
          s.id === sourceId && Object.is(s[field], value)
            ? { ...s, [field]: savedSource[field] }
            : s,
        )
        sourcesRef.current = next
        setSources(next)
        onSourcesUpdate(next)
      } catch (error) {
        toastError(error, 'Failed to save')
        throw error
      }
    },
    [problemId, updateProblemSourceMutation, onSourcesUpdate],
  )

  /**
   * Apply a field edit, save it, and undo exactly that edit if the save fails.
   *
   * The controls that save the moment they are touched go through here rather
   * than pairing a local update with a save themselves, because marking an
   * original is not a one-row edit — it clears whatever row held the mark. A
   * caller undoing only its own field would leave the section badging nobody
   * while the database still credits the old row, and `onSourcesUpdate` fires
   * only on success, so the Statement tab's preview would disagree with both.
   *
   * The undo is a targeted inverse computed against the *latest* rows, never a
   * snapshot taken before the request. A title typed while this save was in
   * flight is not this edit's to roll back, and a debounced save that landed
   * meanwhile is not this edit's to contradict: restoring a whole pre-edit
   * array would put the editor out of step with the row the server holds, with
   * nothing to reconcile it until a refetch.
   *
   * The toast is `handleSaveSourceField`'s, not this function's — one save,
   * one message. Rethrows so `AutoSaveToggle` still reaches its own error
   * state.
   */
  const commitSourceField = useCallback(
    async <K extends SavableSourceField>(
      sourceId: string,
      field: K,
      value: ProblemSource[K],
    ): Promise<void> => {
      const before = sourcesRef.current
      const target = before.find((s) => s.id === sourceId)
      if (!target) return

      // Identified before the edit and remembered by id, so the inverse can
      // find these rows again however the array has moved on since.
      const clearedIds = new Set(
        field === 'isOriginal' && value === true
          ? before
              .filter((s) => s.id !== sourceId && s.isOriginal)
              .map((s) => s.id)
          : [],
      )

      const rewrite = (
        rows: ProblemSource[],
        fieldValue: ProblemSource[K],
        wasOriginal: boolean,
      ): ProblemSource[] =>
        rows.map((s) => {
          if (s.id === sourceId) return { ...s, [field]: fieldValue }
          return clearedIds.has(s.id) ? { ...s, isOriginal: wasOriginal } : s
        })

      // Applied in the same tick as the click. The resolver picks the marked
      // row with `.find()`, so two rows briefly marked would badge the first
      // by array order while the second row's switch already read ON.
      const optimistic = rewrite(before, value, false)
      sourcesRef.current = optimistic
      setSources(optimistic)

      try {
        await handleSaveSourceField(sourceId, field, value)
      } catch (error) {
        const reverted = rewrite(sourcesRef.current, target[field], true)
        sourcesRef.current = reverted
        setSources(reverted)
        throw error
      }
    },
    [handleSaveSourceField],
  )

  /**
   * Commits run one at a time.
   *
   * `commitSourceField` reads the current rows to compute both its optimistic
   * edit and the inverse that undoes it, so a second commit that starts
   * mid-flight reads the first one's *optimistic* array and remembers an
   * unsaved value as the one the server holds. Mark one row original, mark a
   * second before the first answers, have both rejected, and the section ends
   * up badging a row the database does not credit — with `onSourcesUpdate`
   * never fired, so the Statement tab's preview disagrees with both.
   *
   * Queuing makes every commit's starting point a settled one, which is what
   * the inverse needs to be correct. It costs nothing in the ordinary case:
   * the chain is already resolved unless a save is genuinely in flight, and
   * each toggle shows its own optimistic state meanwhile.
   */
  const commitChainRef = useRef<Promise<void>>(Promise.resolve())
  const handleCommitSourceField = useCallback(
    <K extends SavableSourceField>(
      sourceId: string,
      field: K,
      value: ProblemSource[K],
    ): Promise<void> => {
      const run = commitChainRef.current.then(() =>
        commitSourceField(sourceId, field, value),
      )
      // The chain itself must not carry the rejection forward, or one failed
      // save would strand every commit after it. The caller still gets `run`,
      // which rejects.
      commitChainRef.current = run.then(
        () => undefined,
        () => undefined,
      )
      return run
    },
    [commitSourceField],
  )

  // Get available sources to add (not already added)
  // Combines API sources with DEFAULT_SOURCES fallback for backwards compatibility
  const availableSourcesToAdd = useMemo(() => {
    const existingSourceNames = new Set(sources.map((s) => s.source))

    // If we have API sources, use those; otherwise fall back to DEFAULT_SOURCES
    if (availableSourcesFromApi.length > 0) {
      return availableSourcesFromApi
        .filter((s) => !existingSourceNames.has(s.name ?? ''))
        .map((s) => ({ name: s.name ?? 'Unknown Source', icon: s.icon }))
    }

    return DEFAULT_SOURCES.filter((ds) => !existingSourceNames.has(ds.name))
  }, [sources, availableSourcesFromApi])

  // Handle when a new source is created via the modal (for existing problems)
  const handleSourceCreated = useCallback(
    (newSource: Source) => {
      // Invalidate the sources list query so it refetches with the new source
      utils.admin.sources.list.invalidate()
      // Close modal
      setAddSourceModalOpen(false)
      // Add it to the problem immediately, passing the ID directly to avoid
      // stale state lookup (React state updates are asynchronous)
      handleAddSource(newSource.name, newSource.icon, newSource.id)
    },
    [handleAddSource, utils.admin.sources.list],
  )

  // Handle when a new source is created via the modal (for new problems)
  const handleNewProblemSourceCreated = useCallback(
    (newSource: Source) => {
      // Invalidate the sources list query so it refetches with the new source
      utils.admin.sources.list.invalidate()
      // Select the new source
      setSelectedSourceType(newSource.name)
      onFormUpdate({
        source: newSource.name,
        sourceId: newSource.id,
        sourceIcon: newSource.icon,
      })
      setAddSourceModalOpen(false)
    },
    [onFormUpdate, utils.admin.sources.list],
  )

  // Get selected source info (check API sources first, then DEFAULT_SOURCES)
  const selectedSourceInfo = useMemo(() => {
    const apiSource = availableSourcesFromApi.find(
      (s) => s.name === selectedSourceType,
    )
    if (apiSource) {
      return { name: apiSource.name ?? 'Unknown Source', icon: apiSource.icon }
    }
    return DEFAULT_SOURCES.find((s) => s.name === selectedSourceType)
  }, [selectedSourceType, availableSourcesFromApi])

  // Combined sources for dropdown (API sources + DEFAULT_SOURCES fallback)
  const allSourcesForDropdown = useMemo(() => {
    if (availableSourcesFromApi.length > 0) {
      return availableSourcesFromApi.map((s) => ({
        id: s.id,
        name: s.name ?? 'Unknown Source',
        icon: s.icon,
      }))
    }
    // DEFAULT_SOURCES don't have IDs - they're just for display when API fails
    return DEFAULT_SOURCES.map((s) => ({
      id: null as string | null,
      name: s.name,
      icon: s.icon,
    }))
  }, [availableSourcesFromApi])

  // ============================================
  // NEW PROBLEM MODE - Show inline form
  // ============================================
  if (!problemId) {
    return (
      <NewProblemSourceForm
        formData={formData}
        onFormUpdate={onFormUpdate}
        selectedSourceType={selectedSourceType}
        setSelectedSourceType={setSelectedSourceType}
        selectedSourceInfo={selectedSourceInfo}
        allSourcesForDropdown={allSourcesForDropdown}
        loadingAvailableSources={loadingAvailableSources}
        shouldShowHighestId={shouldShowHighestId}
        loadingHighestId={loadingHighestId}
        highestNumericId={highestNumericId}
        highestAlphanumericId={highestAlphanumericId}
        addSourceModalOpen={addSourceModalOpen}
        setAddSourceModalOpen={setAddSourceModalOpen}
        onSourceCreated={handleNewProblemSourceCreated}
      />
    )
  }

  // ============================================
  // EXISTING PROBLEM MODE - Show full sources list
  // ============================================
  return (
    <ProblemSourcesManager
      formData={formData}
      sources={sources}
      availableSourcesToAdd={availableSourcesToAdd}
      loadingAvailableSources={loadingAvailableSources}
      savingSource={savingSource}
      expandedSourceId={expandedSourceId}
      setExpandedSourceId={setExpandedSourceId}
      onAddSource={handleAddSource}
      onDeleteSource={handleDeleteSource}
      onLocalSourceUpdate={handleLocalSourceUpdate}
      onCommitSourceField={handleCommitSourceField}
      onSaveSourceField={handleSaveSourceField}
      onSourceCreated={handleSourceCreated}
    />
  )
}
